import { useSocket } from "App/socketContext";
import { useTypedDispatch, useTypedSelector } from "App/Store";
import MessageChat from "components/MessageChat";
import MessageInput from "components/MessageInput";
import { Tournament, TournamentDetails } from "core/models/Tournament";
import {
  addUserToRoom,
  checkUserInGroup,
  getTournamentChatMessages,
} from "core/services/TournamentChat";
import { useEffect, useState, useRef } from "react";
import { userDetails } from "redux/action/userDetailsAction";
import { Button, Drawer, Modal } from "rsuite";
import Cross from "shared/icons/Cross";

interface TournamentChatProps {
  open: boolean;
  onClose(): void;
  tournament: TournamentDetails | Tournament | null;
}

export interface Message {
  id: string;
  text: string;
  time: string;
  sender: "me" | "other";
  date: string;
  profileImage?: string;
  seen?: boolean;
  delivered?: boolean;
  name?: string;
  deletedBy?: string;
  isDeleted?: boolean;
  createdAt?: string;
}
const PAGE_SIZE = 20;

export function TournamentChat({
  open,
  onClose,
  tournament,
}: TournamentChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const { user } = useTypedSelector((state) => state.userDetails);
  const socket = useSocket();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const didFetchMessages = useRef(false);
  const [isDropDown, setIsDropDown] = useState<{
    open: boolean;
    messageId: string;
  }>({
    open: false,
    messageId: "",
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState({
    open: false,
    messageId: "",
  });
  const [isUserInGroup, setUserInGroup] = useState(false);
  const dispatch = useTypedDispatch();

  useEffect(() => {
    if (!tournament?.id) return;
  
    setMessages([]);
    setPage(1);
    setHasMore(true);
    didFetchMessages.current = false;
    setUserInGroup(false);
  }, [tournament?.id]);

  function openDropdownModal(messageId: string) {
    setIsDropDown({ open: true, messageId });
  }

  function closeDropdownModal() {
    setIsDropDown({ open: false, messageId: "" });
  }

  function openDeleteModal(messageId: string) {
    setIsDeleteModalOpen({ open: true, messageId: messageId });
  }

  const checkUser = async () => {
    if (tournament?.id && user?.id) {
      const response = await checkUserInGroup({
        roomId: tournament?.id,
        userId: user?.id,
      });

      setUserInGroup(response?.isInRoom);
    }
  };

  useEffect(() => {
    if (!user || !user.id) return;

    checkUser();
  }, [tournament?.id, user?.id]);

  const joinRoomUser = async () => {
    if (!tournament?.id || !user?.id) return;

    const response = await addUserToRoom({
      roomId: tournament?.id,
      userId: user?.id,
    });

    if (response.success === true) {
      setUserInGroup(true);
    }
  };

  useEffect(() => {
    if (!socket) return;
    if (!tournament?.id) return;
    if (!isUserInGroup) return;
  
    const roomId = tournament.id;
  
    socket.emit("joinRoom", { roomId });
  
    return () => {
      socket.emit("leaveRoom", { roomId });
    };
  }, [socket, tournament?.id, isUserInGroup]);

  const fetchMessages = async (currentPage: number) => {
    if (!tournament || !isUserInGroup) return;

    const response = await getTournamentChatMessages({
      roomId: tournament.id,
      page: currentPage,
      limit: PAGE_SIZE,
    });

    const newMessages = response.messages.map((val: any) => ({
      sender: user.id?.toString() === val.sender.toString() ? "me" : "other",
      date: new Date(val.createdAt).toISOString().slice(0, 10),
      id: val._id.toString(),
      text: val.content,
      time: new Date(val.createdAt).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      profileImage: val.senderMedia,
      name: val.senderName,
      isDeleted: val.isDeleted || false,
      deletedBy: val.deletedBy || "",
      createdAt: val.createdAt,
    }));

    setMessages((prev) => [...prev, ...newMessages]);
    setHasMore(Boolean(response?.hasMore));
    setPage(currentPage);
  };

  useEffect(() => {
    if (socket && tournament?.id && isUserInGroup) {
      if (!didFetchMessages.current && tournament?.id) {
        didFetchMessages.current = true;
        fetchMessages(1);
      }
    }
  }, [socket, tournament?.id, isUserInGroup]);

  const loadMoreMessages = () => {
    if (!isLoading && hasMore) {
      fetchMessages(page + 1);
    }
  };

  useEffect(() => {
    if (!user?.id || !socket) return;

    const handleNewMessage = (message: any) => {
      const newMessage: Message = {
        sender: user.id === message.sender ? "me" : "other",
        date: new Date(message.createdAt).toISOString().slice(0, 10),
        id: message.id.toString(),
        text: message.content,
        time: new Date(message.createdAt).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        profileImage: message.senderMedia,
        name: message.senderName,
        isDeleted: message?.isDeleted || false,
        deletedBy: message?.deletedBy || "",
        createdAt: message?.createdAt,
      };

      setMessages((prev) => [...prev, newMessage]);
    };

    socket.on("newMessageGroup", handleNewMessage);

    return () => {
      socket.off("newMessageGroup", handleNewMessage);
    };
  }, [socket, user]);

  useEffect(() => {
    const container = document.querySelector(".chat-message-container");
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = (text: string) => {
    if (!text.trim() || !tournament) return;

    socket?.emit("sendMessageGroup", {
      roomId: tournament.id,
      content: text,
      attachement: [],
    });
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen({ open: false, messageId: "" });
  };

  const handleConfirmDeleteMessage = () => {
    socket?.emit("deleteMessage", {
      roomId: tournament?.id,
      messageId: isDeleteModalOpen.messageId,
      deletedBy: user?.id,
    });

    setIsDropDown({ open: false, messageId: "" });
    setIsDeleteModalOpen({ open: false, messageId: "" });
  };

  useEffect(() => {
    socket?.on("messageDeleted", (data) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === data.id
            ? {
                ...msg,
                text: "",
                attachments: [],
                isDeleted: data.isDeleted,
                deletedBy: data.deletedBy,
              }
            : msg
        )
      );
    });

    return () => {
      socket?.off("messageDeleted");
    };
  }, [socket]);

  useEffect(() => {
    dispatch(userDetails());
  }, [dispatch]);

  return (
    <>
      <Drawer size="xs" placement="right" show={open} onHide={onClose}>
        <div className="chat-container">
          <div className="chat-header">
            <div className="chat-title">Chat</div>
            <div onClick={onClose} className="close-chat">
              <Cross color="#ffffff" fontSize={20} />
            </div>
          </div>

          <div className="chat-profile-header">
            <img
              src={tournament?.content?.icon}
              alt="profile"
              className="profile-img"
              loading="lazy"
            />
            <div className="profile-name">
              {tournament?.title || "Nova Spider"}
            </div>
          </div>

          <div className="chat-message-container">
            {isUserInGroup ? (
              <MessageChat
                messages={messages}
                isDropDown={isDropDown}
                openDropdownModal={openDropdownModal}
                closeDropdownModal={closeDropdownModal}
                openDeleteModal={openDeleteModal}
                hasMore={hasMore}
                loadMoreMessages={loadMoreMessages}
              />
            ) : (
              <div className="join-chat">
                <Button className="join-chat-btn" onClick={joinRoomUser}>
                  {" "}
                  Join Chat
                </Button>
              </div>
            )}

            {/* <div ref={messageEndRef} /> */}
          </div>
          <div className={!isUserInGroup ? "disable-chat-input" : ""}>
            <MessageInput
              placeholder="Write a message..."
              className="message-input"
              onSendMessage={handleSendMessage}
            />
          </div>
        </div>
      </Drawer>
      <Modal
        overflow
        backdrop="static"
        show={isDeleteModalOpen.open}
        onHide={closeDeleteModal}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Delete Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="delete-modal-content">
            <p className="delete-text">
              Are you sure you want to delete this message?
            </p>
            <div className="delete-actions">
              <Button
                className="cancel-btn"
                onClick={closeDeleteModal}
                appearance="ghost"
                color="blue"
              >
                Cancel
              </Button>
              <Button
                className="delete-btn"
                appearance="primary"
                color="red"
                onClick={handleConfirmDeleteMessage}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
